import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Card,
  CardTitle,
  DataTable,
  EmptyState,
  PageBody,
  Spinner,
  StatCard,
  type DataTableColumn,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { fetchPipelineStats, type PipelineBatch } from '@/lib/pipelineStats.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';

export const Route = createFileRoute('/_app/admin/pipeline')({
  staticData: { orgScope: 'none' },
  component: PipelineDashboardPage,
});

/** A stage with nothing left to do is finished, not zero per cent finished. */
function percent(done: number, total: number): number {
  return total === 0 ? 100 : Math.round((done / total) * 100);
}

function count(value: number): string {
  return value.toLocaleString();
}

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'ok'] as const;

const SEVERITY_TONE = {
  critical: 'danger',
  high: 'warning',
  medium: 'warning',
  low: 'neutral',
  ok: 'success',
} as const;

const ANALYZERS: { key: string; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'profile', label: 'Profile' },
  { key: 'memory', label: 'Memory' },
];

const ACTIVE_BATCH_STATUSES = ['pending', 'validating', 'in_progress', 'finalizing'];

const BATCH_TYPE_LABELS: Record<string, string> = {
  classification: 'Classification',
  session_analysis: 'Session Analysis',
  conversation_review: 'Conversation Review',
};

function batchStatusTone(status: string) {
  if (status === 'completed') return 'success' as const;
  if (status === 'in_progress' || status === 'validating' || status === 'finalizing') {
    return 'info' as const;
  }
  if (status === 'pending') return 'warning' as const;
  if (status === 'failed' || status === 'expired' || status === 'cancelled') {
    return 'danger' as const;
  }
  return 'neutral' as const;
}

const batchColumns: DataTableColumn<PipelineBatch>[] = [
  {
    key: 'type',
    header: 'Type',
    render: (batch) => (
      <Badge tone='neutral'>{BATCH_TYPE_LABELS[batch.type] ?? batch.type}</Badge>
    ),
  },
  {
    key: 'batch_id',
    header: 'Batch ID',
    render: (batch) => (
      <span className='font-mono text-xs text-ink-secondary'>
        {batch.batch_id.substring(0, 20)}...
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (batch) => <Badge tone={batchStatusTone(batch.status)}>{batch.status}</Badge>,
  },
  {
    key: 'created_at',
    header: 'Created',
    render: (batch) => formatDateTime(batch.created_at),
  },
  {
    key: 'completed_at',
    header: 'Completed',
    render: (batch) => formatDateTime(batch.completed_at),
  },
];

/**
 * How far behind the batch pipeline is, stage by stage.
 *
 * Four readings and a job list. Each stage is a fraction of one denominator —
 * messages, questions, sessions — so a stage at 100% is one with nothing
 * waiting, not one that has ever seen everything.
 */
export function PipelineDashboardPage() {
  const { data: stats, isPending } = useQuery({
    queryKey: ['pipelineStats'],
    queryFn: fetchPipelineStats,
  });

  if (isPending || !stats) {
    return (
      <>
        <PageHeader title='Pipeline' />
        <PageBody>
          <Spinner className='size-5 text-ink-muted' />
        </PageBody>
      </>
    );
  }

  const { classification, session_analysis: sessions, reviews, batches } = stats;

  const detectedPct = percent(classification.detected, classification.total_messages);
  const classifiedPct = percent(
    classification.fully_classified,
    classification.questions,
  );
  const sessionPct = percent(sessions.fully_analyzed, sessions.total_sessions);
  const reviewedTotal = reviews.total + reviews.needing_review;
  const reviewPct = percent(reviews.total, reviewedTotal);

  const activeBatches = batches.filter((batch) =>
    ACTIVE_BATCH_STATUSES.includes(batch.status),
  ).length;

  return (
    <>
      <PageHeader title='Pipeline' />

      <PageBody className='flex flex-col gap-5'>
        <div className='grid grid-cols-1 gap-4 mobile:grid-cols-2 agenda:grid-cols-3'>
          <StatCard
            label='Detection'
            value={`${detectedPct}%`}
            hint={
              <>
                {count(classification.detected)} / {count(classification.total_messages)}{' '}
                messages
                {classification.needing_detection > 0 &&
                  ` · ${count(classification.needing_detection)} awaiting`}
              </>
            }
            info='Identifies which user messages are questions using direct API calls'
          />

          <StatCard
            label='Classification'
            value={`${classifiedPct}%`}
            hint={
              <>
                {count(classification.fully_classified)} /{' '}
                {count(classification.questions)} questions
                {classification.pending_in_batch > 0 &&
                  ` · ${count(classification.pending_in_batch)} in batch`}
                {classification.needing_classification > 0 &&
                  ` · ${count(classification.needing_classification)} queued`}
              </>
            }
            info='Categorizes detected questions with context via OpenAI Batch API'
          />

          <StatCard
            label='Session Analysis'
            value={`${sessionPct}%`}
            hint={
              <>
                {count(sessions.fully_analyzed)} / {count(sessions.total_sessions)}{' '}
                sessions
                {ANALYZERS.map((analyzer) => (
                  <span key={analyzer.key}>
                    {` · ${count(sessions.analyzers[analyzer.key] ?? 0)} ${analyzer.label}`}
                  </span>
                ))}
              </>
            }
            info='Extracts summaries, profile memories, and action plans from sessions via OpenAI Batch API'
          />

          <StatCard
            label='Conversation Reviews'
            value={`${reviewPct}%`}
            hint={
              <>
                {count(reviews.total)} / {count(reviewedTotal)} sessions reviewed
                {reviews.needing_review > 0 &&
                  ` · ${count(reviews.needing_review)} awaiting`}
              </>
            }
            info={
              <>
                QA scores analyzed sessions for satisfaction, bugs and guardrail issues
                via OpenAI Batch API
                <span className='mt-2 flex flex-wrap gap-1'>
                  {SEVERITY_ORDER.filter(
                    (severity) => reviews.by_severity[severity] > 0,
                  ).map((severity) => (
                    <Badge key={severity} tone={SEVERITY_TONE[severity]}>
                      {count(reviews.by_severity[severity])} {severity}
                    </Badge>
                  ))}
                </span>
              </>
            }
          />
        </div>

        <Card as='section' className='flex flex-col gap-3.5'>
          <div className='flex items-center justify-between gap-3'>
            <CardTitle>OpenAI Batch Jobs</CardTitle>
            {activeBatches > 0 && <Badge tone='info'>{activeBatches} active</Badge>}
          </div>

          <DataTable<PipelineBatch>
            label='OpenAI batch jobs'
            columns={batchColumns}
            rows={batches}
            rowKey={(batch) => batch.id}
            emptyState={<EmptyState title='No batch jobs found' headingLevel='h3' />}
          />
        </Card>
      </PageBody>
    </>
  );
}
